import moment, { Moment } from 'moment';

import { ProgramTaskStatus, States } from '@vestahealthcare/common/enums';
import {
  CCMTask,
  PODShuffleMember,
  PodConfig,
  PodMetricsSummary,
  PodMetricsSummaryEmployee,
  PodMetricsSummaryForecast,
  PodRule,
  PodRuleTrigger,
  PodShuffleAsyncTask,
  PodShuffleEmployeeForecast,
  PodShufflePodForecast,
  ProgramTask,
} from '@vestahealthcare/common/models';
import { DATE_FORMAT } from '@vestahealthcare/common/utils/constants';

import Api, {
  PaginatedResponse,
  PaginatedResponseWithTime,
  PaginationParams,
} from 'dash/src/services/Api';

import {
  BULK_EDIT_ERROR_NP_STATE,
  BULK_EDIT_ERROR_RN_STATE,
  BulkEditValidationError,
} from './PatientServices';
import { mergeValidations } from './RosterServices';

export type GetPodConfigParams = {
  active?: boolean;
} & PaginationParams;

export const fetchPodConfigs = async (
  params?: GetPodConfigParams,
): Promise<PaginatedResponse<PodConfig>> => {
  const {
    pods: { items, pagination },
  } = await Api.getv2('pods', {
    ...(params || {}),
    sort: params?.sort || 'name asc',
  });
  return {
    items: items.map((item: any) => new PodConfig(item)),
    pagination,
  };
};

export const fetchAllPodConfigs = async (): Promise<PodConfig[]> => {
  const { items } = await fetchPodConfigs({
    active: true,
    limit: -1,
    sort: 'name asc',
  });
  return items;
};

export type PodConfigEmployeeSlot = {
  employeeId?: number;
  id?: number;
  maxMembers?: number;
  role: string;
};

export type CreatePodConfigParams = {
  employees: PodConfigEmployeeSlot[];
  languages: string[];
  managerId: number;
  maxMembers?: number;
  name: string;
  programExtensionIds: number[];
  referralSourceIds?: number[];
  states: string[];
};

export const createPodConfig = async (params?: CreatePodConfigParams) =>
  await Api.postv2JSON('pods', params);

export type UpdatePodConfigParams = Partial<
  CreatePodConfigParams & {
    active: boolean;
  }
>;

export const updatePodConfig = async (
  id: number,
  { employees, ...params }: UpdatePodConfigParams,
) =>
  await Api.patchv2JSON(
    `pods/${id}`,
    {
      employees: employees?.map(({ id, employeeId, maxMembers, ...item }) => ({
        id: id || undefined,
        employeeId: employeeId || undefined,
        maxMembers: maxMembers || undefined,
        ...item,
      })),
      ...params,
    },
    { showToast: true },
  );

export type GetPodRuleParams = {
  active?: boolean;
} & PaginationParams;

export const fetchPodRules = async (
  params?: GetPodRuleParams,
): Promise<PaginatedResponse<PodRule>> => {
  const {
    taskDefinitions: { items, pagination },
  } = await Api.getv2('program-tasks/definitions', params);
  return {
    items: items.map((item: any) => new PodRule(item)),
    pagination,
  };
};

export const fetchAllPodRules = async (): Promise<PodRule[]> => {
  const { items } = await fetchPodRules({ active: true, limit: -1 });
  // FIXME: Remove when BE filter works
  return items.filter(({ active }) => active);
};

export const fetchPodTriggers = async (): Promise<PodRuleTrigger[]> => {
  const {
    triggers: { items },
  } = await Api.getv2('triggers', { sort: 'name asc' });
  return items.map((item: any) => new PodRuleTrigger(item));
};

export type CreatePodRuleParams = {
  additional: boolean;
  confirmationType?: string;
  confirmationTypeReason?: string;
  default: boolean;
  description?: string;
  flexible: boolean;
  maxAttempts: number;
  name: string;
  overriddenBy?: number[];
  programExtensionId?: number;
  requireConfirmation: boolean;
  roles: string[];
  rolloverAfterPeriod: boolean;
  spacing?: number;
  triggers?: {
    triggerName: string;
    delayValue?: number;
    delayType?: string;
    status?: string;
  }[];
};

export type UpdatePodRuleParams = Partial<
  CreatePodRuleParams & {
    active: boolean;
  }
>;

export type GetCCMTaskParams = PaginationParams & {
  assigneeId?: number[];
  engagementLevel?: string[];
  language?: string[];
  taskDefinitionId?: number[];
};

export const fetchProgramTasksQueue = async (
  params: GetCCMTaskParams,
): Promise<PaginatedResponseWithTime<CCMTask>> => {
  const {
    ccmTasks: { items, pagination, lastUpdatedAt },
  } = await Api.getv2('program-tasks/queue', params);
  return {
    items: items.map((x: any) => new CCMTask(x)),
    lastUpdatedAt: lastUpdatedAt ? moment.unix(lastUpdatedAt) : undefined,
    pagination,
  };
};

export const attemptCCMTask = async (id: number): Promise<ProgramTask> => {
  const { task } = await Api.patchv2JSON(
    `program-tasks/${id}/actions/move-to-in-progress`,
  );
  return new ProgramTask(task);
};

export const updateCCMTaskAssignee = async (
  id: number,
  assigneeId: number,
): Promise<ProgramTask> => {
  const { task } = await Api.patchv2JSON(
    `program-tasks/${id}/actions/reassign`,
    {
      assigneeId,
    },
  );
  return new ProgramTask(task);
};

export const forceOverrideProgramTask = async (
  id: number,
): Promise<ProgramTask> => {
  const { task } = await Api.patchv2JSON(
    `program-tasks/${id}/actions/force-override`,
    {
      taskDefinitionId: PodRule.WELLNESS_CHECK_IN_ID,
    },
  );
  return new ProgramTask(task);
};

export const overrideProgramTask = async (
  id: number,
  taskDefinitionId: number,
  assigneeId: number,
): Promise<ProgramTask> => {
  const { task } = await Api.patchv2JSON(
    `program-tasks/${id}/actions/override-and-reassign`,
    {
      assigneeId,
      taskDefinitionId,
    },
  );
  return new ProgramTask(task);
};

export const completeChildProgramTask = async (
  id: number,
  status: ProgramTaskStatus,
) =>
  await Api.patchv2JSON(`program-tasks/${id}/actions/close`, {
    status: status.value,
  });

export type GetPodMetricsParams = {
  month?: Moment;
} & PaginationParams;

export const fetchPodMetricsSummary = async ({
  month,
  ...rest
}: GetPodMetricsParams): Promise<PodMetricsSummary[]> => {
  const isCurrentMonth = month?.isSame(moment(), 'month');
  if (isCurrentMonth) {
    const {
      podSummaries: { items },
    } = await Api.getv2('pods/summary', rest);
    return items.map((item: any) => new PodMetricsSummary(item));
  }
  const {
    podSummaries: { items },
  } = await Api.getv2('pods/summary/snapshot', {
    month: month?.format(DATE_FORMAT),
    ...rest,
  });
  return items.map((item: any) => new PodMetricsSummary(item));
};

export const fetchPodMetricsForecast = async ({
  month,
  ...rest
}: GetPodMetricsParams): Promise<PodMetricsSummaryForecast[]> => {
  const {
    podForecast: { items },
  } = await Api.getv2('pods/forecast', {
    month: month?.format(DATE_FORMAT),
    ...rest,
  });
  return items.map((item: any) => new PodMetricsSummaryForecast(item));
};

export const fetchPodMetricEmployees = async (
  id: number,
): Promise<PodMetricsSummaryEmployee[]> => {
  const {
    podEmployees: { items },
  } = await Api.getv2(`pods/summary/${id}/employees`);
  return items.map((item: any) => new PodMetricsSummaryEmployee(item));
};

const prepareParams = ({
  additional,
  rolloverAfterPeriod,
  default: isDefault,
  flexible,
  ...params
}: CreatePodRuleParams | UpdatePodRuleParams) => ({
  isAdditional: additional,
  isRolloverAfterPeriod: rolloverAfterPeriod,
  isDefault,
  isFlexible: flexible,
  ...params,
});

export const createPodRule = async (params: CreatePodRuleParams) =>
  await Api.postv2JSON('program-tasks/definitions', prepareParams(params));

export const updatePodRule = async (id: number, params: UpdatePodRuleParams) =>
  await Api.patchv2JSON(
    `program-tasks/definitions/${id}`,
    prepareParams(params),
  );

export type GetProgramTaskParam = PaginationParams & {
  assigneeId?: number[];
  attempt?: number[];
  childTaskDefinitionId?: string[];
  excludedTaskId?: number[];
  isChild?: boolean;
  language?: string[];
  lastAppointmentType?: string[];
  memberId?: number;
  month?: Moment;
  nextAppointmentType?: string[];
  podId?: (number | string)[];
  programExtensionId?: number;
  state?: string[];
  statusAge?: string;
  statusId?: number[];
  taskDateFrom?: string;
  taskDateTo?: string;
  taskDefinitionId?: (number | string)[];
  taskId?: number[];
  touchpointReason?: string[];
};

export type PodTaskBulkCommonParams = {
  filter?: {
    assigneeIds?: number[];
    excludedTaskIds?: number[];
    languages?: string[];
    month: Moment;
    podIds?: number[] | string[];
    states?: string[];
    statusIds?: string[];
    taskDefinitionIds: number[] | string[];
  };

  taskIds?: number[];
};

type PodTaskBulkReassignParams = PodTaskBulkCommonParams & {
  assigneeId: number;
};

export const fetchPogramTasks = async ({
  month,
  taskDateFrom,
  taskDateTo,
  ...params
}: GetProgramTaskParam): Promise<PaginatedResponse<ProgramTask>> => {
  const {
    tasks: { items, pagination },
  } = await Api.getv2(`program-tasks`, {
    ...params,
    month: undefined,
    taskDateFrom: month
      ? month.startOf('month').format(DATE_FORMAT)
      : taskDateFrom,
    taskDateTo: month ? month.endOf('month').format(DATE_FORMAT) : taskDateTo,
  });
  return {
    items: items.map((item: any) => new ProgramTask(item)),
    pagination,
  };
};

export const bulkReassignPodTasks = async (params: PodTaskBulkReassignParams) =>
  await Api.patchv2JSON('program-tasks/actions/reassign', {
    ...params,
    filter: params.filter
      ? {
          ...params.filter,
          month: undefined,
          taskDateFrom: params.filter.month
            .startOf('month')
            .format(DATE_FORMAT),
          taskDateTo: params.filter.month.endOf('month').format(DATE_FORMAT),
        }
      : undefined,
  });

type PodTaskBulkOverrideParams = PodTaskBulkCommonParams & {
  assigneeId: number;
  taskDefinitionId: number;
};

export const bulkOverridePodTasks = async (params: PodTaskBulkOverrideParams) =>
  await Api.patchv2JSON('program-tasks/actions/override-and-reassign', {
    ...params,
    filter: params.filter
      ? {
          ...params.filter,
          month: undefined,
          taskDateFrom: params.filter.month
            .startOf('month')
            .format(DATE_FORMAT),
          taskDateTo: params.filter.month.endOf('month').format(DATE_FORMAT),
        }
      : undefined,
  });

type PodTaskBulkForceOverrideParams = PodTaskBulkCommonParams & {};

export const bulkForceOverridePodTasks = async (
  params: PodTaskBulkForceOverrideParams,
) =>
  await Api.patchv2JSON('program-tasks/actions/force-override-and-reassign', {
    ...params,
    filter: params.filter
      ? {
          ...params.filter,
          month: undefined,
          taskDateFrom: params.filter.month
            .startOf('month')
            .format(DATE_FORMAT),
          taskDateTo: params.filter.month.endOf('month').format(DATE_FORMAT),
        }
      : undefined,
    taskDefinitionId: PodRule.WELLNESS_CHECK_IN_ID,
  });

type SchedulePODShuffleParams = {
  podId?: number;
  month?: string;
  podManagerId?: number;
  npOwnerId?: number;
  rnOwnerId?: number;
  healthCoachIds?: number[];
  careCoordinatorIds?: number[];
  engagementOwnerIds?: number[];
  podChange: boolean;
};

type FilterPODShuffleParams = {
  filter?: {
    excludedPodShuffleMemberIds?: number[];
    podId?: number;
    month?: string;
  };
};

export type PODShuffleResponse = {
  errors: BulkEditValidationError[];
};

const TIME_POLLING = 5000;

const podShuffleReady = async (uuid: string): Promise<any> => {
  const {
    status,
    response,
  }: {
    status: number;
    response: any;
  } = await Api.getv2Status('/pod-shuffle/poll', {
    uuid,
  });

  if (status === 202) {
    return await new Promise((res, rej) => {
      setTimeout(
        () => podShuffleReady(uuid).then(res).catch(rej),
        TIME_POLLING,
      );
    });
  }

  return response;
};

export const schedulePODShuffleAsync = async (
  memberIds: number[],
  params: SchedulePODShuffleParams,
): Promise<PODShuffleResponse> => {
  const { uuid } = await Api.postv2JSON(`/pod-shuffle/async`, {
    memberIds,
    ...params,
  });

  const { podShuffle } = await podShuffleReady(uuid);

  return {
    ...podShuffle,
    errors: podShuffle.errors?.map((item: any) => ({
      type: item.type,
      extraData: [BULK_EDIT_ERROR_NP_STATE, BULK_EDIT_ERROR_RN_STATE].includes(
        item.type,
      )
        ? item.extraData?.map((item: string) => States.byKey[item])
        : item.extraData,
    })),
  };
};

export const editPODShuffleAsync = async (
  memberIds: number[],
  params: SchedulePODShuffleParams & FilterPODShuffleParams,
): Promise<PODShuffleResponse> => {
  const { uuid } = await Api.patchv2JSON(`/pod-shuffle/async`, {
    podShuffleMemberIds: memberIds,
    ...params,
  });

  const { podShuffle } = await podShuffleReady(uuid);

  return {
    ...podShuffle,
    errors: podShuffle.errors?.map((item: any) => ({
      type: item.type,
      extraData: [BULK_EDIT_ERROR_NP_STATE, BULK_EDIT_ERROR_RN_STATE].includes(
        item.type,
      )
        ? item.extraData?.map((item: string) => States.byKey[item])
        : item.extraData,
    })),
  };
};

type PODShuffleMembersParams = PaginationParams & {
  podId?: number;
  date?: string;
  statusId?: string;
};

export const getPODShuffleMembers = async (
  params: PODShuffleMembersParams,
): Promise<PaginatedResponse<PODShuffleMember>> => {
  const {
    podShuffleMembers: { items, pagination },
  } = await Api.getv2(`/pod-shuffle`, {
    ...params,
    month: params.date,
  });

  return {
    items: items.map((item: any) => new PODShuffleMember(item)),
    pagination,
  };
};

export const deletePODShuffleMembers = async (
  payload: {
    podShuffleMemberIds: number[];
  } & FilterPODShuffleParams,
): Promise<PODShuffleResponse> => {
  return await Api.postv2JSON(`/pod-shuffle/remove`, {
    ...payload,
  });
};

type CheckPODShuffleParams = {
  memberIds?: number[];
  podId?: number;
};

export const checkPODShuffle = async (
  payload: CheckPODShuffleParams,
): Promise<{ podChange: boolean }> => {
  return await Api.postv2JSON(`/patients/check-pod-change`, {
    ...payload,
  });
};

type CheckPODShuffleForecastParams = {
  podShuffleMemberIds?: number[];
  filter?: {
    month?: string;
    excludedPodShuffleMemberIds?: number[];
    podId?: number;
  };
  podId?: number;
};

export const checkPODShuffleForecast = async (
  payload: CheckPODShuffleForecastParams,
): Promise<{ podChange: boolean }> => {
  return await Api.postv2JSON(`/pod-shuffle/check-pod-change`, {
    ...payload,
  });
};

export const getPODShuffleEmployeeForecast = async (
  podId: number,
): Promise<PodShuffleEmployeeForecast[]> => {
  const { podShuffleEmployeeForecast } = await Api.getv2(
    `/pod-shuffle/forecast/${podId}/employees`,
  );

  return (
    podShuffleEmployeeForecast?.items?.map(
      (item: any) => new PodShuffleEmployeeForecast(item),
    ) || []
  );
};

export const getPODShufflePODForecast = async (): Promise<
  PodShufflePodForecast[]
> => {
  const { podShufflePodForecast } = await Api.getv2(
    `/pod-shuffle/forecast/pods`,
  );

  return (
    podShufflePodForecast?.items?.map(
      (item: any) => new PodShufflePodForecast(item),
    ) || []
  );
};

export const getPodShuffleRoster = async (
  params?: PaginationParams,
): Promise<PaginatedResponse<PodShuffleAsyncTask>> => {
  const {
    podShuffleRosters: { items, pagination },
  } = await Api.getv2(`/pod-shuffle-roster`, params);

  return {
    items: items.map((i: any) => new PodShuffleAsyncTask(i)),
    pagination,
  };
};

const checkValidateFilePodShuffleReady = async (uuid: string): Promise<any> => {
  const {
    status,
    response,
  }: {
    status: number;
    response: any;
  } = await Api.getv2Status(`/pod-shuffle-roster/validate/poll`, {
    uuid,
  });

  if (status === 202) {
    return await new Promise((res, rej) => {
      setTimeout(
        () => checkValidateFilePodShuffleReady(uuid).then(res).catch(rej),
        TIME_POLLING,
      );
    });
  }

  return response;
};

export const bulkPodShuffleValidate = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await Api.postv2File(
    'pod-shuffle-roster/validate/async',
    formData,
  );

  if (response.validationResult) {
    return mergeValidations(response.validationResult.items);
  }
  const { validationResult } = await checkValidateFilePodShuffleReady(
    response.uuid || '',
  );
  return mergeValidations(validationResult.items);
};

const checkProcessFilePodShuffleReady = async (uuid: string): Promise<any> => {
  const {
    status,
    response,
  }: {
    status: number;
    response: any;
  } = await Api.getv2Status(`/pod-shuffle-roster/process/poll`, {
    uuid,
  });

  if (status === 202) {
    return await new Promise((res, rej) => {
      setTimeout(
        () => checkProcessFilePodShuffleReady(uuid).then(res).catch(rej),
        TIME_POLLING,
      );
    });
  }

  return response;
};

export const bulkPodShuffleUpload = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await Api.postv2File(
    'pod-shuffle-roster/process/async',
    formData,
  );

  if (response.validationResult) {
    return {
      validationResult: mergeValidations(response.validationResult.items),
    };
  }

  if (response.uuid) {
    checkProcessFilePodShuffleReady(response.uuid);
  }

  return null;
};
